import { Directive, Input, OnChanges, QueryList, SimpleChanges, ViewContainerRef, ComponentRef, inject } from '@angular/core';

import { HisGlobalDataService } from '../../../core/service/base/his-global-data.service';
import { COMPONENT_BASE } from '../../model/component-base.interface';
import { DynamicComponent, GUIComponent } from '../../model/dynamic-content';
import { DynamicFormContainerComponent } from '../form-container/dynamic-form-container.component';

@Directive( {
    selector: '[hisDynamicContent]',
    standalone: false
} )
export class HisDynamicContentDirective implements OnChanges {
    private hostComponent = inject<object>( COMPONENT_BASE );
    private hostViewContainerRef = inject( ViewContainerRef );
    private globalDataService = inject( HisGlobalDataService );

    @Input( 'hisDynamicContent' ) hisDynamicContent: string;
    @Input( 'dynamicComponent' ) dynamicComponent: DynamicComponent;

    // Keep track of list of generated components for removal purposes
    private components: ComponentRef<DynamicFormContainerComponent>[] = [];

    private static readonly FORMGROUPVALUE = 'dynamicformGroupValue';
    private static readonly QUERYLIST_FIELD_CONTAINER = 'fieldViewContainerList';
    private static readonly QUERYLIST_FIELDSET_CONTAINER = 'fieldsetViewContainerList';
    private queryListOfContainer: QueryList<ViewContainerRef> = new QueryList<ViewContainerRef>();

    ngOnChanges( changes: SimpleChanges ) {
        if ( changes.dynamicComponent ) {
            // reset formGroupValues
            if ( !this.dynamicComponent ) {
                this.globalDataService.setData( HisDynamicContentDirective.FORMGROUPVALUE, null );
                // this.components = [];
            }
            if ( ( this.hostComponent as Record<string, any> )[HisDynamicContentDirective.QUERYLIST_FIELDSET_CONTAINER] ) {
                this.queryListOfContainer =
                    ( this.hostComponent as Record<string, any> )[HisDynamicContentDirective.QUERYLIST_FIELDSET_CONTAINER];
                if ( this.queryListOfContainer && this.dynamicComponent ) {
                    let fieldsetsOfTab: GUIComponent = this.getFieldsetsOfTab();
                    // if dynamic component configuration contains fields for given hisPanel
                    if ( fieldsetsOfTab ) {
                        this.setUpDynamicComponentFieldsets( fieldsetsOfTab.children );
                    }
                }
            } else {
                this.queryListOfContainer =
                    ( this.hostComponent as Record<string, any> )[HisDynamicContentDirective.QUERYLIST_FIELD_CONTAINER];
                if ( this.queryListOfContainer && this.dynamicComponent && this.hisDynamicContent ) {
                    let fieldsOfFieldset: GUIComponent = this.getFieldsOfFieldset();
                    // console.log( 'dynamic content fieldset: ', fieldsOfFieldset );
                    // if dynamic component configuration contains fields for given hisPanel
                    if ( fieldsOfFieldset ) {
                        this.setUpDynamicComponentFields( fieldsOfFieldset.fields );
                    }
                }
            }
        }
    }

    /* find tab and field-set children for hisPanel in dynamicComponent */
    private getFieldsetsOfTab(): GUIComponent {
        this.hostViewContainerRef.element.nativeElement.tabHisDynamicContentKey = this.hisDynamicContent;
        return this.dynamicComponent.guiComponent?.children.find(
            ( tabChild: GUIComponent ) => tabChild.name === this.hisDynamicContent );
    }

    /* find tab and field-set children for hisPanel in dynamicComponent */
    private getFieldsOfFieldset(): GUIComponent {
        let fieldsetChildren: GUIComponent[] = [];
        // console.log( '#HisDynamicContentDirective.getFieldsOfFieldset() - ( this.hostViewContainerRef as any )._lContainer: ', ( this.hostViewContainerRef as any )._lContainer );

        let tabHisDynamicContentKey = ( this.hostViewContainerRef as any )._lContainer[3][0].tabHisDynamicContentKey;
        if ( tabHisDynamicContentKey ) {
            this.setFieldsetChildrenFromHostViewContainerRef( tabHisDynamicContentKey, fieldsetChildren );
            // console.log('#HisDynamicContentDirective.getFieldsOfFieldset() - ( this.hostViewContainerRef as any )._lContainer[3][3][0].attributes: ', ( this.hostViewContainerRef as any )._lContainer[3][3][0].attributes );
        } else {
            tabHisDynamicContentKey = ( this.hostViewContainerRef as any )._lContainer[3].find(
                ( viewContainerRefElement: any ) => viewContainerRefElement !== null && Array.isArray( viewContainerRefElement )
                    && viewContainerRefElement[0]?.tabHisDynamicContentKey )?.tabHisDynamicContentKey;
            if ( tabHisDynamicContentKey ) {
                this.setFieldsetChildrenFromHostViewContainerRef( tabHisDynamicContentKey, fieldsetChildren );
            } else {
                this.dynamicComponent.guiComponent?.children.forEach(
                    ( tabChild: GUIComponent ) => fieldsetChildren = [...fieldsetChildren, ...tabChild.children] );
            }
        }
        return fieldsetChildren.find(
            ( fieldsetChild: GUIComponent ) => fieldsetChild.name === this.hisDynamicContent );
    }

    private setFieldsetChildrenFromHostViewContainerRef( tabHisDynamicContentKey: string, fieldsetChildren: GUIComponent[] ) {
        this.dynamicComponent.guiComponent?.children.find(
            ( tabChild: GUIComponent ) => {
                if ( tabChild.name === tabHisDynamicContentKey ) {
                    fieldsetChildren.push( ...tabChild.children );
                }
            } );
    }

    /* create dynamic field-set components inside the ng-template */
    private setUpDynamicComponentFieldsets( fieldsets: GUIComponent[] ) {
        this.queryListOfContainer.forEach( ( viewContainerRef: ViewContainerRef, viewContainerIndex: number ) => {
            this.clearViewContainerRefComponent( viewContainerRef ); // clear viewContainerRef and component array
            fieldsets.sort( ( fieldset1, fieldset2 ) => {
                const s1 = fieldset1.sortorder;
                const s2 = fieldset2.sortorder;
                // Nulls last
                if ( s1 === null || s1 === undefined ) {
                    return ( s2 === null || s2 === undefined ) ? 0 : 1;
                }
                if ( s2 === null || s2 === undefined ) {
                    return -1;
                }
                return s1 - s2;
            } );
            fieldsets.forEach( ( fieldset: GUIComponent ) => {
                fieldset.fields.sort( ( f1, f2 ) => {
                    const s1 = f1.sortorder;
                    const s2 = f2.sortorder;
                    // Nulls last
                    if ( s1 === null || s1 === undefined ) {
                        return ( s2 === null || s2 === undefined ) ? 0 : 1;
                    }
                    if ( s2 === null || s2 === undefined ) {
                        return -1;
                    }
                    return s1 - s2;
                } );
                let componentId = 'fieldset_' + fieldset.name;
                if ( viewContainerIndex === fieldset.position ) {
                    let component: ComponentRef<DynamicFormContainerComponent> = this.createComponent( viewContainerRef, componentId );
                    component.instance.dynamicComponentTabName = this.hisDynamicContent;
                    component.instance.dynamicComponent = this.dynamicComponent;
                    component.instance.createDynamicFieldConfig( null, fieldset, this.dynamicComponent.dynamicObjects );

                    // subscribe to child output event
                    component.instance.outputEvent.subscribe( ( formGroupValue: Record<string, any> ) => {
                        fieldset.fields.forEach( field => {
                            this.setGlobalDataService( formGroupValue[fieldset.name], field.name, fieldset.name );
                        } );
                    } );
                }
            } );
        } );
    }

    /* create dynamic field components inside the ng-template */
    private setUpDynamicComponentFields( fields: GUIComponent[] ) {
        this.queryListOfContainer.forEach( ( viewContainerRef: ViewContainerRef, viewContainerIndex: number ) => {
            this.clearViewContainerRefComponent( viewContainerRef ); // clear viewContainerRef and component array
            fields.sort( ( f1, f2 ) => {
                const s1 = f1.sortorder;
                const s2 = f2.sortorder;
                // Nulls last
                if ( s1 === null || s1 === undefined ) {
                    return ( s2 === null || s2 === undefined ) ? 0 : 1;
                }
                if ( s2 === null || s2 === undefined ) {
                    return -1;
                }
                return s1 - s2;
            } );
            fields.forEach( ( field: GUIComponent ) => {
                let componentId = 'field_' + field.attributeName;
                if ( viewContainerIndex === field.position ) {
                    console.log( '#HisDynamicContentDirective.setUpDynamicComponentFields - this.dynamicComponent.dynamicObject: ', this.dynamicComponent.dynamicObjects );

                    let component: ComponentRef<DynamicFormContainerComponent> = this.createComponent( viewContainerRef, componentId );
                    component.instance.dynamicComponent = this.dynamicComponent;
                    component.instance.createDynamicFieldConfig( null, field, this.dynamicComponent.dynamicObjects );

                    // subscribe to child output event
                    component.instance.outputEvent.subscribe( ( formGroupValue: any ) => {
                        this.setGlobalDataService( formGroupValue, field.name, this.hisDynamicContent );
                    } );
                }
            } );
        } );
    }

    /* clear viewContainerRef and components array */
    private clearViewContainerRefComponent( viewContainerRef: ViewContainerRef ) {
        viewContainerRef.clear();
        this.components = [];
    }

    /* create component for view container reference with id */
    private createComponent( viewContainerRef: ViewContainerRef, componentId: string ): ComponentRef<DynamicFormContainerComponent> {
        let component: ComponentRef<DynamicFormContainerComponent> = viewContainerRef.createComponent( DynamicFormContainerComponent );
        component.instance.componentId = componentId;
        this.components.push( component );
        return component;
    }

    /* store form group values in global data service */
    private setGlobalDataService( formGroupValue: Record<string, any>, fieldName: string, fieldSetName: string ) {
        let globalDataServiceFormGroupValues = this.globalDataService.getData( HisDynamicContentDirective.FORMGROUPVALUE );
        if ( globalDataServiceFormGroupValues ) {
            if ( globalDataServiceFormGroupValues[fieldSetName] ) {
                globalDataServiceFormGroupValues[fieldSetName][fieldName] = formGroupValue[fieldName];
            } else {
                Object.assign( globalDataServiceFormGroupValues, { [fieldSetName]: formGroupValue } );
            }
        } else {
            this.globalDataService.setData( HisDynamicContentDirective.FORMGROUPVALUE, { [fieldSetName]: formGroupValue } );
        }
    }
}
